import type { Identifier } from "ra-core";
import type { ConversationType } from "@/modules/types";

export const buildDmKey = (memberA: Identifier, memberB: Identifier) =>
  [String(memberA), String(memberB)].sort().join(":");

export const getConversationTypeLabel = (type: ConversationType) => {
  if (type === "team_dm") return "Direct message";
  if (type === "project") return "Project team";
  if (type === "client") return "Client";
  return type;
};

export const isMediaPlaceholderBody = (
  body: string | null | undefined,
  mediaCount: number,
) => {
  if (mediaCount === 0) return false;
  const trimmed = body?.trim() ?? "";
  if (!trimmed) return true;
  if (trimmed === "Photo" || trimmed === "Attachment") return true;
  return /^\d+ photos$/.test(trimmed);
};

export const getMessageMediaUrls = (message: {
  media_url?: string | null;
  media_urls?: string[] | null;
}) => {
  const fromArray = (message.media_urls ?? []).filter(Boolean);
  if (fromArray.length > 0) return fromArray;
  if (message.media_url) return [message.media_url];
  return [];
};

export const buildMessagePreview = (message: {
  body?: string | null;
  media_url?: string | null;
  media_urls?: string[] | null;
  is_internal_note?: boolean | null;
}) => {
  if (message.is_internal_note) {
    return message.body?.trim() || "Internal note";
  }
  const mediaCount = getMessageMediaUrls(message).length;
  if (mediaCount > 0) {
    return message.body?.trim() || "Sent an attachment";
  }
  return message.body?.trim() || "New message";
};

export const formatMessageTime = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** Local calendar day key for grouping thread messages (date dividers). */
export const getMessageDayKey = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};

export const formatMessageDateDivider = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "long",
      day: "numeric",
    });
  }
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const sortConversationsByActivity = <
  T extends {
    last_message_at?: string | null;
    updated_at?: string | null;
    id: unknown;
  },
>(
  conversations: T[],
) =>
  [...conversations].sort((left, right) => {
    const leftKey = left.last_message_at ?? left.updated_at ?? "";
    const rightKey = right.last_message_at ?? right.updated_at ?? "";
    const diff = rightKey.localeCompare(leftKey);
    return diff !== 0 ? diff : String(right.id).localeCompare(String(left.id));
  });
